import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

interface Correction {
  id: string;
  patient_temp_id: string;
  ancien_nom: string;
  ancien_prenom: string;
  nouveau_nom: string;
  nouveau_prenom: string;
  statut: string;
  demandeur_id: string;
  admin_id: string | null;
  created_at: string;
  updated_at: string;
  patients_temporaires: {
    nom: string;
    prenom: string;
    numero_dossier: string;
  } | null;
}

export default function AdminCorrectionsPatientsPage() {
  const navigate = useNavigate();
  const { user, userRoles } = useAuth();
  const [corrections, setCorrections] = useState<Correction[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'toutes' | 'en_attente' | 'approuvee' | 'rejetee'>('en_attente');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const isAdmin = userRoles?.includes('super_admin');

  useEffect(() => {
    if (!user) { navigate('/login', { replace: true }); return; }
    if (!isAdmin) { navigate('/', { replace: true }); return; }
    loadCorrections();
  }, [user, isAdmin]);

  const loadCorrections = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('corrections_temporaires')
        .select('*, patients_temporaires!inner(nom, prenom, numero_dossier)')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCorrections(data || []);
    } catch (err) {
      console.error('Erreur chargement corrections:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (correction: Correction) => {
    setProcessingId(correction.id);
    setMessage(null);
    try {
      // Mettre à jour le patient temporaire
      const { error: updateErr } = await supabase
        .from('patients_temporaires')
        .update({
          nom: correction.nouveau_nom,
          prenom: correction.nouveau_prenom,
        })
        .eq('id', correction.patient_temp_id);

      if (updateErr) throw updateErr;

      // Marquer la correction comme approuvée
      const { error: corrErr } = await supabase
        .from('corrections_temporaires')
        .update({
          statut: 'approuvee',
          admin_id: user?.id,
          updated_at: new Date().toISOString(),
        })
        .eq('id', correction.id);

      if (corrErr) throw corrErr;

      setMessage({ type: 'success', text: `Correction approuvée : ${correction.nouveau_nom} ${correction.nouveau_prenom}` });
      await loadCorrections();
    } catch (err: unknown) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Erreur lors de l\'approbation' });
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (correction: Correction) => {
    setProcessingId(correction.id);
    setMessage(null);
    try {
      const { error } = await supabase
        .from('corrections_temporaires')
        .update({
          statut: 'rejetee',
          admin_id: user?.id,
          updated_at: new Date().toISOString(),
        })
        .eq('id', correction.id);

      if (error) throw error;

      setMessage({ type: 'success', text: 'Correction rejetée.' });
      await loadCorrections();
    } catch (err: unknown) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Erreur lors du rejet' });
    } finally {
      setProcessingId(null);
    }
  };

  const filteredCorrections = filter === 'toutes'
    ? corrections
    : corrections.filter((c) => c.statut === filter);

  const counts = {
    toutes: corrections.length,
    en_attente: corrections.filter((c) => c.statut === 'en_attente').length,
    approuvee: corrections.filter((c) => c.statut === 'approuvee').length,
    rejetee: corrections.filter((c) => c.statut === 'rejetee').length,
  };

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Corrections Patients Temporaires</h1>
            <p className="text-sm text-gray-500 mt-1">
              Validez ou rejetez les demandes de correction de noms
            </p>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
            Retour
          </button>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-2xl flex items-start gap-2 ${
            message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            <i className={`${message.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} mt-0.5 w-5 h-5 flex items-center justify-center`}></i>
            <span className="text-sm">{message.text}</span>
          </div>
        )}

        {/* Filtres */}
        <div className="flex items-center gap-2 mb-6">
          {(['toutes', 'en_attente', 'approuvee', 'rejetee'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                filter === f
                  ? 'bg-teal-600 text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-teal-300'
              }`}
            >
              {f === 'toutes' ? 'Toutes' : f === 'en_attente' ? 'En attente' : f === 'approuvee' ? 'Approuvées' : 'Rejetées'}
              <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs font-bold ${
                filter === f ? 'bg-white/20' : 'bg-gray-100'
              }`}>
                {counts[f]}
              </span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line text-4xl text-teal-500 animate-spin w-8 h-8 flex items-center justify-center"></i>
            <span className="ml-3 text-gray-500">Chargement des corrections...</span>
          </div>
        ) : filteredCorrections.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 bg-teal-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-double-line text-2xl text-teal-500 w-6 h-6 flex items-center justify-center"></i>
            </div>
            <p className="text-lg font-medium text-gray-700 mb-1">
              {filter === 'en_attente' ? 'Aucune correction en attente' : 'Aucune correction trouvée'}
            </p>
            <p className="text-sm text-gray-500">
              {filter === 'en_attente' ? 'Toutes les demandes ont été traitées.' : 'Essayez un autre filtre.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredCorrections.map((correction) => (
              <div
                key={correction.id}
                className={`bg-white rounded-2xl border overflow-hidden transition-all ${
                  correction.statut === 'en_attente' ? 'border-amber-200 shadow-sm' :
                  correction.statut === 'approuvee' ? 'border-green-200' : 'border-red-200'
                }`}
              >
                <div className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Statut badge */}
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          correction.statut === 'en_attente' ? 'bg-amber-100 text-amber-700' :
                          correction.statut === 'approuvee' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {correction.statut === 'en_attente' ? 'En attente' :
                           correction.statut === 'approuvee' ? 'Approuvée' : 'Rejetée'}
                        </span>
                        <span className="text-xs text-gray-400">
                          Demandée le {new Date(correction.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>

                      {/* Patient info */}
                      <div className="flex items-center gap-3 mb-4">
                        <span className="px-2 py-0.5 bg-gray-100 rounded-md font-mono text-xs text-gray-600">
                          {correction.patients_temporaires?.numero_dossier || 'N/A'}
                        </span>
                      </div>

                      {/* Correction details */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <p className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wide">Actuel</p>
                          <p className="text-lg font-semibold text-gray-900">
                            <span className="line-through text-gray-400">{correction.ancien_nom}</span>
                            {' '}
                            <span className="line-through text-gray-400">{correction.ancien_prenom}</span>
                          </p>
                        </div>
                        <div className="bg-teal-50 rounded-xl p-4 border border-teal-100">
                          <p className="text-xs text-teal-600 mb-2 font-medium uppercase tracking-wide">Corrigé</p>
                          <p className="text-lg font-semibold text-teal-800">
                            {correction.nouveau_nom} {correction.nouveau_prenom}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions - only for pending */}
                  {correction.statut === 'en_attente' && (
                    <div className="flex items-center gap-3 mt-4 pt-4 border-t border-gray-100">
                      <button
                        onClick={() => handleApprove(correction)}
                        disabled={processingId === correction.id}
                        className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                      >
                        {processingId === correction.id ? (
                          <><i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>Traitement...</>
                        ) : (
                          <><i className="ri-check-line w-4 h-4 flex items-center justify-center"></i>Approuver</>
                        )}
                      </button>
                      <button
                        onClick={() => handleReject(correction)}
                        disabled={processingId === correction.id}
                        className="flex items-center gap-2 px-5 py-2.5 bg-white border-2 border-red-200 text-red-600 rounded-xl hover:bg-red-50 transition-all font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-close-line w-4 h-4 flex items-center justify-center"></i>
                        Rejeter
                      </button>
                    </div>
                  )}

                  {/* Approved/Rejected info */}
                  {correction.statut !== 'en_attente' && (
                    <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2 text-sm text-gray-500">
                      <i className={`w-4 h-4 flex items-center justify-center ${correction.statut === 'approuvee' ? 'ri-check-line text-green-500' : 'ri-close-line text-red-500'}`}></i>
                      {correction.statut === 'approuvee' ? 'Correction appliquée' : 'Correction refusée'}
                      {correction.updated_at && (
                        <span className="text-gray-400">
                          le {new Date(correction.updated_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}